import type { LeadEventType } from "../shared/types";
import type { RouterOutputs } from "@/trpc";

/**
 * One row of the mappable-attribute catalog, derived from the router output so it
 * is literally the backend's `HUBSPOT_LEAD_ATTRIBUTES` — the same constant the sync
 * reads. Never hand-declare it: the dropdown and the runtime drifted once already
 * (12 attributes at runtime vs 8 in the UI).
 */
export type HubspotLeadAttribute =
  RouterOutputs["crmSelfConfig"]["getHubspotLeadAttributes"][number];

/** One option of an attribute's picking list: what Palomma stores, and its label. */
export type HubspotLeadAttributeValue = NonNullable<
  HubspotLeadAttribute["knownValues"]
>[number];

// Every LeadEvent that actually emits at runtime. Most are lead-lifecycle
// transitions in core/lead/service.ts that pass a `leadEventType` to
// `transitionPipelinePosition` (and therefore publish to LeadEventTopic). The
// HubSpot event→stage mapping is constrained to this list so a merchant can only
// map events that will genuinely fire a sync — no silent no-op rows. Notably it
// includes `lead.property-info-collected` ("Captación: Información Recolectada"),
// the key event for captación-pipeline leads.
//
// `lead.created` is the exception to the "from the lead service" rule: it never
// fires from core/lead/service.ts, but it DOES fire — from the eight lead-creation
// paths (logic/whatsapp/lead.ts, functions/broadcast/queue.ts,
// lambdaAnalytics/sendTemplate.ts, logic/merchantApi/outboundLead.ts,
// pautaOutbound.ts, emailOutbound.ts, merchantApi/development/outboundMessage.ts,
// messageEcho.ts). Mapping it makes HubSpot's contact + deal get CREATED at the
// lead's birth, with later events updating them.
//
// `first-message-*` and `call-ended` remain excluded: they genuinely never fire
// into this dispatcher.
export const HUBSPOT_MAPPABLE_EVENTS = [
  "lead.created",
  "lead.redirected-to-human",
  "lead.appointment-intention",
  "lead.appointment-created",
  "lead.property-info-collected",
  "lead.maintenance-requested",
  "lead.discarded",
  "lead.inactivated",
] as const satisfies readonly LeadEventType[];

// Each mappable event is emitted by exactly one pipeline-position transition
// (core/lead/service.ts markLeadAs* → SYSTEM_POSITIONS_MAP). An event is only
// relevant to a Palomma pipeline that actually contains that target position —
// e.g. `propertyInfoCollected` lives only in Captación, `maintenanceRequested`
// only in Mantenimiento, the appointment positions only in Comercial. The portal
// uses this to show, per pipeline, just the events that pipeline can fire (so
// "Captación: Información Recolectada" never appears under Comercial, etc.).
// Values are the position `status`/`id` (identical for system positions).
export const HUBSPOT_EVENT_TARGET_POSITION: Record<
  (typeof HUBSPOT_MAPPABLE_EVENTS)[number],
  string
> = {
  // `SYSTEM_POSITIONS_MAP.NEW` is in EVERY pipeline template (core/pipeline/types.ts),
  // so Lead creado shows for every pipeline with no special-casing.
  "lead.created": "new",
  "lead.redirected-to-human": "redirected",
  "lead.appointment-intention": "appointmentIntent",
  "lead.appointment-created": "appointmentPending",
  "lead.property-info-collected": "propertyInfoCollected",
  "lead.maintenance-requested": "maintenanceRequested",
  "lead.discarded": "discarded",
  "lead.inactivated": "inactive",
};

export type HubspotPipeline = {
  id: string;
  name: string;
};

export type HubspotStage = {
  id: string;
  name: string;
};

export type HubspotContactProperty = {
  name: string;
  label: string;
  type: string;
  options: { value: string; label: string }[];
  // HubSpot's "this property enforces uniqueness" flag. Only such a property is a
  // legal `idProperty` for batch/upsert, so the dedup selects filter on it.
  hasUniqueValue?: boolean;
};

export type HubspotEventMapping = {
  event: LeadEventType;
  pipelineId: string;
  dealStage: string;
  createDeal?: boolean;
};

export type HubspotContactFieldMapping = {
  leadAttr: string;
  hubspotProperty: string;
  constantValue?: string;
  valueMap?: Record<string, string>;
};
